"use client";

import { createContext } from "react";

import type { EvmProvider, SolanaProvider } from "@/lib/mudwater";
import type { PickerEntry, WalletId } from "@/lib/wallet/solana/types";

/**
 * The stable surface every wallet consumer sees. Demo and live providers each
 * publish this same shape, and `src/test/live-fixtures.tsx` constructs it
 * directly, so adding a member is a breaking change to those fixtures.
 */
export interface WalletState {
  connected: boolean;
  /**
   * True while a returning holder's wallet may still reattach silently, so a
   * page can hold its connected layout rather than flash the disconnected one.
   * Absent (false) wherever there is no silent reattach.
   */
  reattaching?: boolean;
  /** False while the configured login provider initializes. */
  solanaReady?: boolean;
  setConnected: (connected: boolean) => void;
  evmAddress: string | null;
  solanaAddress: string | null;
  /** Display-only labels for the selected providers; never identity/recipient keys. */
  evmWalletName?: string;
  solanaWalletName?: string;
  ethereum: EvmProvider | null;
  solana: SolanaProvider | null;
  connectEvm: () => Promise<void>;
  addEvmNetwork: () => Promise<void>;
  connectSolana: () => Promise<void>;
  /**
   * Disconnects the external Solana asset wallet only. The EVM
   * connection is claim-page state and is deliberately left alone: someone who
   * signs out and back in should not have to reconnect it to see eligibility.
   */
  disconnect: () => void;
  /** Dropping the ApeChain wallet is owned by the claim page. */
  disconnectEvm: () => void;
  /** Disconnect, then open the explicit EVM picker once its fresh render is ready. */
  changeEvm?: (signal?: AbortSignal) => Promise<void>;
  /**
   * Proves the signed-in wallet to the game server, which then seats this
   * browser under its name. Resolves false when the seat is still unnamed (the
   * holder declined to sign). Absent in the demo and builds without a login.
   */
  claimGameIdentity?: () => Promise<boolean | void>;
  /**
   * Phantom only: a fresh Sign-In-With-Solana for the connected wallet (one
   * prompt), even when this browser already proved it. False when declined.
   */
  proveWallet?: () => Promise<boolean>;
  /**
   * The generic Solana wallet layer only (`NEXT_PUBLIC_MUDWATER_SOLANA_WALLETS`
   * set): the wallet in use, connecting one by id, and the picker's rows.
   * Absent on today's Phantom path and in Glyph builds.
   */
  solanaWallet?: WalletId;
  connectSolanaWith?: (id: WalletId, expectedAddress?: string) => Promise<void>;
  /** Abandon an unanswered generic connection without logging out the active wallet. */
  cancelSolanaConnection?: () => void;
  solanaWallets?: readonly PickerEntry[];
}

export const WalletContext = createContext<WalletState | null>(null);
